import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { KeyRound, RefreshCw, User, Mail, Phone, Clock, ChevronRight, ShieldAlert, Wand2, Copy, Check, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { getAuthHeader } from '../../helpers/auth';

const API = process.env.REACT_APP_API_BASE_URL; // already includes /api

const getRolePill = (role) => {
  const r = (role || '').toLowerCase();
  const map = {
    admin:     { bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.22)', color: '#A78BFA' },
    sales:     { bg: 'rgba(52,211,153,0.10)',  border: 'rgba(52,211,153,0.20)',  color: '#34D399' },
    operation: { bg: 'rgba(59,130,246,0.10)',  border: 'rgba(59,130,246,0.20)',  color: '#60A5FA' },
    customer:  { bg: 'var(--nd-w06)', border: 'var(--nd-w10)', color: '#94A3B8' },
  };
  const found = Object.entries(map).find(([k]) => r.includes(k));
  const style = found ? found[1] : { bg: 'rgba(251,191,36,0.10)', border: 'rgba(251,191,36,0.20)', color: '#FBBF24' };
  return (
    <span style={{ background: style.bg, border: `1px solid ${style.border}`, color: style.color, padding: '2px 9px', borderRadius: '20px', fontSize: '10px', fontWeight: '700', letterSpacing: '0.06em' }}>
      {role || 'User'}
    </span>
  );
};

const timeAgo = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d)) return '—';
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const genPassword = () => {
  // Readable temp password: avoids ambiguous chars (0/O, 1/l/I).
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let out = '';
  for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
};

const td = { padding: '14px 20px', fontSize: '13px', color: '#94A3B8', borderBottom: '1px solid var(--nd-w04)', verticalAlign: 'top' };
const th = { padding: '12px 20px', fontSize: '10px', fontWeight: '700', color: 'var(--nd-tx25)', textTransform: 'uppercase', letterSpacing: '0.12em', textAlign: 'left', borderBottom: '1px solid var(--nd-w05)', background: 'var(--nd-w02)' };

function RequestRow({ req, onResolved, onDismissed }) {
  const [pwd, setPwd] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const fullName = `${req.first_name || ''} ${req.last_name || ''}`.trim() || '(unknown user)';

  const copyPwd = async () => {
    if (!pwd) return;
    try {
      await navigator.clipboard.writeText(pwd);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable — no-op */ }
  };

  const resolve = async () => {
    if (pwd.trim().length < 6) {
      toast.error('Temporary password must be at least 6 characters.');
      return;
    }
    if (!req.user_id) {
      toast.error('This request is not linked to a user.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-requests/${req.id}/resolve`, { newPassword: pwd.trim() }, getAuthHeader());
      toast.success(`Password reset for ${fullName}. Send the temporary password to the user.`);
      onResolved(req.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to set password.');
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    if (!window.confirm(`Dismiss the reset request from ${fullName}? No password will change.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-requests/${req.id}/dismiss`, {}, getAuthHeader());
      toast.info('Request dismissed.');
      onDismissed(req.id);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to dismiss request.');
    } finally {
      setBusy(false);
    }
  };

  const iconBtn = (active) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34,
    borderRadius: '8px', border: '1px solid var(--nd-w08)', background: 'var(--nd-w04)',
    color: active ? '#34D399' : 'var(--nd-tx45)', cursor: 'pointer',
  });

  return (
    <tr className="prr-row">
      <td style={td}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: 36, height: 36, minWidth: 36, borderRadius: '10px', background: 'linear-gradient(135deg,#3B82F6,#22D3A5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--nd-text-strong)', fontWeight: 800, fontSize: 13 }}>
            {(req.first_name || req.email || 'U').charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: '700', color: 'var(--nd-text)', fontSize: '14px' }}>{fullName}</div>
            <div style={{ color: 'var(--nd-tx30)', fontSize: '12px' }}>@{req.username || '—'}</div>
            <div style={{ marginTop: 4 }}>{getRolePill(req.user_type)}</div>
          </div>
        </div>
      </td>
      <td style={td}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Mail size={13} color="var(--nd-tx25)"/>{req.user_email || req.email || '—'}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><Phone size={13} color="var(--nd-tx25)"/>{req.mobile_number || '—'}</div>
        </div>
      </td>
      <td style={td}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
          <Clock size={13} color="var(--nd-tx25)"/>{timeAgo(req.requested_at)}
        </div>
        {req.note ? <div style={{ marginTop: 6, fontSize: '12px', color: 'var(--nd-tx45)', fontStyle: 'italic', maxWidth: 220 }}>“{req.note}”</div> : null}
      </td>
      <td style={{ ...td, minWidth: 320 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 150 }}>
            <input
              type="text"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="Temporary password"
              style={{ width: '100%', padding: '9px 36px 9px 12px', background: 'var(--nd-w04)', border: '1px solid var(--nd-w10)', borderRadius: '8px', color: 'var(--nd-text)', fontSize: '13px', outline: 'none', fontFamily: "'DM Mono',monospace", colorScheme: 'dark' }}
            />
            <button title="Copy" onClick={copyPwd} disabled={!pwd} style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: copied ? '#34D399' : 'var(--nd-tx35)', cursor: pwd ? 'pointer' : 'default', padding: 6 }}>
              {copied ? <Check size={14}/> : <Copy size={14}/>}
            </button>
          </div>
          <button title="Generate" onClick={() => setPwd(genPassword())} style={iconBtn(false)}><Wand2 size={15}/></button>
          <button onClick={resolve} disabled={busy} className="prr-btn" style={{ padding: '9px 14px', background: 'linear-gradient(90deg,#3B82F6,#22D3A5)', color: 'var(--nd-text-strong)', border: 'none', borderRadius: '8px', fontWeight: '700', fontSize: '12px', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <KeyRound size={14}/>Reset
          </button>
          <button title="Dismiss" onClick={dismiss} disabled={busy} style={{ ...iconBtn(false), color: '#F87171' }}><X size={15}/></button>
        </div>
      </td>
    </tr>
  );
}

const PasswordResetRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/auth/reset-requests`, getAuthHeader());
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (err?.response?.status === 403) setError('Admin access required to view password reset requests.');
      else setError('Failed to load reset requests.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const removeRow = (id) => setRequests((p) => p.filter((r) => r.id !== id));

  return (
    <div style={{ padding: '14px 20px', fontFamily: "'DM Sans',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap'); @keyframes prr-spin{to{transform:rotate(360deg)}} .prr-row:hover td{background:var(--nd-w02)!important} .prr-btn:hover{opacity:.85}`}</style>

      {/* Header */}
      <div style={{ background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)', borderRadius: '14px', padding: '20px 24px 18px', marginBottom: '18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '700', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--nd-tx25)', marginBottom: '10px' }}>
          <span>Admin</span><ChevronRight size={12} color="var(--nd-w12)"/><span style={{ color: 'rgba(34,211,165,0.55)' }}>Password Reset Requests</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <div>
            <div style={{ fontSize: '26px', fontWeight: '800', color: 'var(--nd-tx85)', letterSpacing: '-0.03em', lineHeight: 1.1, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <KeyRound size={22} color="#3B82F6"/>Password Reset Requests
            </div>
            <div style={{ fontSize: '12px', color: 'var(--nd-tx30)', marginTop: '5px' }}>Field users who tapped “Forgot password?” — set a temporary password and tell them.</div>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right', marginRight: '8px' }}>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '28px', fontWeight: '700', lineHeight: 1, background: 'linear-gradient(90deg,#3B82F6,#22D3A5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{requests.length}</div>
              <div style={{ fontSize: '11px', color: 'var(--nd-tx25)', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Pending</div>
            </div>
            <button onClick={fetchRequests} className="prr-btn" style={{ padding: '10px 16px', background: 'var(--nd-w04)', border: '1px solid var(--nd-w08)', borderRadius: '10px', color: 'var(--nd-tx55)', fontWeight: '700', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '7px', fontFamily: "'DM Sans',sans-serif" }}>
              <RefreshCw size={15} style={loading ? { animation: 'prr-spin 1s linear infinite' } : {}}/>Refresh
            </button>
          </div>
        </div>
        <div style={{ height: '2px', borderRadius: '2px', background: 'linear-gradient(90deg,#3B82F6,#22D3A5,transparent)' }}/>
      </div>

      {/* Table */}
      <div style={{ background: 'var(--nd-surface)', border: '1px solid var(--nd-w06)', borderRadius: '14px', overflow: 'hidden' }}>
        {error && (
          <div style={{ padding: '14px 20px', background: 'rgba(248,113,113,0.07)', borderBottom: '1px solid rgba(248,113,113,0.18)', color: '#F87171', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ShieldAlert size={15}/>{error}
            <button onClick={fetchRequests} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#F87171', cursor: 'pointer', textDecoration: 'underline', fontSize: '13px' }}>Retry</button>
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
            <thead><tr>
              <th style={th}>User</th><th style={th}>Contact</th><th style={th}>Requested</th><th style={th}>Set Temporary Password</th>
            </tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="4" style={{ padding: '60px', textAlign: 'center' }}>
                  <div style={{ width: '36px', height: '36px', border: '3px solid var(--nd-w08)', borderTop: '3px solid #3B82F6', borderRadius: '50%', animation: 'prr-spin 1s linear infinite', margin: '0 auto' }}/>
                  <p style={{ color: 'var(--nd-tx25)', marginTop: '12px', fontSize: '13px' }}>Loading requests…</p>
                </td></tr>
              ) : requests.length === 0 && !error ? (
                <tr><td colSpan="4" style={{ padding: '60px', textAlign: 'center', color: 'var(--nd-tx22)', fontSize: '13px' }}>
                  <User size={26} style={{ opacity: 0.3, marginBottom: 8 }}/><div>No pending password reset requests.</div>
                </td></tr>
              ) : (
                requests.map((req) => (
                  <RequestRow key={req.id} req={req} onResolved={removeRow} onDismissed={removeRow} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetRequests;
